/**
 * Every widget the app can open (unit 26A; `frontend.md` rule 7).
 *
 * **The backend's catalogue test reads this file** (`agent/substrate/widgets.py`):
 * it takes each entry's `key` and `access` from the two lines that open it, and
 * fails if the agent's catalogue disagrees. Keep both there, in that order.
 *
 * `access` is D23's class: an owner widget is left out of a member's launcher,
 * and out of what a member's agent is told. All 21 are built (unit 31B; 22
 * less Briefing, which is an item in Reading's queue since 2026-10-12); a
 * name the registry does not hold gets a tile that says so.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

export type Access = "owner" | "member";

export interface WidgetEntry {
  key: string;
  access: Access;
  title: string;
  /** The unit that built it. */
  unit: number;
  /** In the launcher: Document is opened from Drive or by the agent, never alone. */
  launcher: boolean;
  /** One chunk per widget, loaded when a tile first shows it. */
  component: LazyExoticComponent<ComponentType>;
}

export const REGISTRY: readonly WidgetEntry[] = [
  {
    key: "health",
    access: "member",
    title: "Health",
    unit: 28,
    launcher: true,
    component: lazy(() => import("./health/Health").then((m) => ({ default: m.Health }))),
  },
  {
    key: "turner",
    access: "member",
    title: "Turner",
    unit: 28,
    launcher: true,
    component: lazy(() => import("./turner/Turner").then((m) => ({ default: m.Turner }))),
  },
  {
    key: "finance",
    access: "member",
    title: "Finance",
    unit: 29,
    launcher: true,
    component: lazy(() => import("./finance/Finance").then((m) => ({ default: m.Finance }))),
  },
  {
    key: "canvas",
    access: "member",
    title: "Canvas",
    unit: 26,
    launcher: true,
    component: lazy(() => import("./canvas/Canvas").then((m) => ({ default: m.Canvas }))),
  },
  {
    key: "todos",
    access: "member",
    title: "Todos",
    unit: 27,
    launcher: true,
    component: lazy(() => import("./todos/Todos").then((m) => ({ default: m.Todos }))),
  },
  {
    key: "drive",
    access: "member",
    title: "Drive",
    unit: 26,
    launcher: true,
    component: lazy(() => import("./drive/Drive").then((m) => ({ default: m.Drive }))),
  },
  {
    key: "journal",
    access: "member",
    title: "Journal",
    unit: 27,
    launcher: true,
    component: lazy(() => import("./journal/Journal").then((m) => ({ default: m.Journal }))),
  },
  {
    key: "worldmap",
    access: "member",
    title: "World Map",
    unit: 31,
    launcher: true,
    component: lazy(() => import("./worldmap/WorldMap").then((m) => ({ default: m.WorldMap }))),
  },
  {
    key: "digitalwellbeing",
    access: "member",
    title: "Digital Wellbeing",
    unit: 31,
    launcher: true,
    component: lazy(() => import("./wellbeing/Wellbeing").then((m) => ({ default: m.Wellbeing }))),
  },
  {
    key: "recipes",
    access: "member",
    title: "Recipes",
    unit: 29,
    launcher: true,
    component: lazy(() => import("./recipes/Recipes").then((m) => ({ default: m.Recipes }))),
  },
  {
    key: "goals",
    access: "member",
    title: "Goals",
    unit: 30,
    launcher: true,
    component: lazy(() => import("./goals/Goals").then((m) => ({ default: m.Goals }))),
  },
  {
    key: "piano",
    access: "member",
    title: "Piano",
    unit: 30,
    launcher: true,
    component: lazy(() => import("./piano/Piano").then((m) => ({ default: m.Piano }))),
  },
  {
    key: "atlas",
    access: "member",
    title: "Atlas",
    unit: 29,
    launcher: true,
    component: lazy(() => import("./atlas/Atlas").then((m) => ({ default: m.Atlas }))),
  },
  {
    key: "notes",
    access: "member",
    title: "Notes",
    unit: 27,
    launcher: true,
    component: lazy(() => import("./notes/Notes").then((m) => ({ default: m.Notes }))),
  },
  {
    key: "reading",
    access: "member",
    title: "Reading",
    unit: 30,
    launcher: true,
    component: lazy(() => import("./reading/Reading").then((m) => ({ default: m.Reading }))),
  },
  {
    key: "domestic",
    access: "member",
    title: "Domestic",
    unit: 29,
    launcher: true,
    component: lazy(() => import("./domestic/Domestic").then((m) => ({ default: m.Domestic }))),
  },
  {
    key: "executive",
    access: "owner",
    title: "Executive",
    unit: 29,
    launcher: true,
    component: lazy(() => import("./executive/Executive").then((m) => ({ default: m.Executive }))),
  },
  {
    key: "audiobooks",
    access: "member",
    title: "Audiobooks",
    unit: 30,
    launcher: true,
    component: lazy(() =>
      import("./audiobooks/Audiobooks").then((m) => ({ default: m.Audiobooks })),
    ),
  },
  {
    key: "internals",
    access: "member",
    title: "Internals",
    unit: 31,
    launcher: true,
    component: lazy(() => import("./internals/Internals").then((m) => ({ default: m.Internals }))),
  },
  {
    key: "settings",
    access: "member",
    title: "Settings",
    unit: 26,
    launcher: true,
    component: lazy(() => import("./settings/Settings").then((m) => ({ default: m.Settings }))),
  },
  {
    key: "document",
    access: "member",
    title: "Document",
    unit: 26,
    launcher: false,
    component: lazy(() => import("./document/Document").then((m) => ({ default: m.Document }))),
  },
];

const BY_KEY = new Map(REGISTRY.map((entry) => [entry.key, entry]));

export function widget(key: string): WidgetEntry | undefined {
  return BY_KEY.get(key);
}

/** The widgets this person can open (D23). */
export function widgetsFor(owner: boolean): readonly WidgetEntry[] {
  return owner ? REGISTRY : REGISTRY.filter((entry) => entry.access === "member");
}
